import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import './styles.css'; // We'll move the original CSS here
import './custom.css'; // Import our new custom styling
import Header from './components/Header';
import Timeline from './components/Timeline';
import TaskModal from './components/TaskModal';
import Login from './components/Login';
import AcceptInvite from './components/AcceptInvite';
import InviteManager from './components/InviteManager';
import ProjectManager from './components/ProjectManager';
import UserManager from './components/UserManager';
import { taskService, authService } from './services/api';

// Dashboard component that includes the timeline and task management
function Dashboard({
  tasks,
  isModalOpen,
  currentDate,
  user,
  handlePrevDay,
  handleNextDay,
  handleAddTask,
  handleEditTask,
  handleDeleteTask,
  handleStatusChange,
  setIsModalOpen,
  setEditingTask,
  editingTask,
  handleLogout,
  onLoadMoreTasks,
  hasMoreTasks,
  isLoadingMore
}) {
  return (
    <div style={{ 
      // maxWidth: '1200px', 
      // margin: '0 auto', 
      // padding: '20px',
      // backgroundColor: 'white',
      // borderRadius: '8px',
      // boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      // marginTop: '20px'
    }}>
      <Header 
        onPrevDay={handlePrevDay}
        onNextDay={handleNextDay}
        currentDate={currentDate.toLocaleDateString('en-US', { 
          weekday: 'long', 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        })}
        user={user}
        onLogout={handleLogout}
      />
      
      <Timeline 
        tasks={tasks} 
        onStatusChange={handleStatusChange} 
        onAddTask={() => {
          setEditingTask(null);
          setIsModalOpen(true);
        }}
        onEditTask={handleEditTask}
        onDeleteTask={handleDeleteTask}
        currentDate={currentDate}
        onLoadMoreTasks={onLoadMoreTasks}
        hasMoreTasks={hasMoreTasks}
        isLoadingMore={isLoadingMore}
      />
      
      <TaskModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTask(null);
        }}
        onSubmit={editingTask ? handleEditTask : handleAddTask}
        initialData={editingTask}
        date={currentDate.toISOString().split('T')[0]}
      />
    </div>
  );
}

function App() {
  const [tasks, setTasks] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')) || null);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);
  
  // Infinite scroll state
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTasks, setHasMoreTasks] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const TASKS_PER_PAGE = 20;

  // Check authentication on mount and when token changes
  useEffect(() => {
    const checkAuthStatus = async (forceCheck = false) => {
      const token = localStorage.getItem('token');
      if (token) {
        // If user is already set and this isn't a forced check, skip the validation
        if (user && !forceCheck) {
          setLoading(false);
          return;
        }

        try {
          const response = await authService.getProfile();
          setUser(response.data);
          // Also store user data from profile fetch
          localStorage.setItem('user', JSON.stringify(response.data));
        } catch (error) {
          console.warn('Token validation failed:', error);
          
          // Only log out on specific authentication errors, not network errors
          if (error.response && (error.response.status === 401 || error.response.status === 403)) {
            console.log('Authentication failed, logging out...');
            handleLogout();
          } else {
            // For network errors or other issues, try to use cached user data
            console.log('Network/server error, trying to use cached user data...');
            const cachedUser = localStorage.getItem('user');
            if (cachedUser && !user) {
              try {
                const userData = JSON.parse(cachedUser);
                setUser(userData);
                console.log('Using cached user data:', userData);
              } catch (parseError) {
                console.error('Error parsing cached user data:', parseError);
                // Only logout if we can't use cached data and have no current user
                if (!user) {
                  handleLogout();
                }
              }
            }
          }
        }
      } else {
        // No token found, ensure user state is cleared
        setUser(null);
      }
      setLoading(false);
    };

    // Initial auth check
    checkAuthStatus(true);

    // Listen for localStorage changes from other tabs
    const handleStorageChange = (event) => {
      console.log('Storage change detected:', event.key, event.newValue);
      
      if (event.key === 'token') {
        if (event.newValue) {
          // Token was added/updated in another tab - check auth status
          console.log('Token added/updated in another tab, checking auth status...');
          checkAuthStatus(true);
        } else {
          // Token was removed in another tab - log out
          console.log('Token removed in another tab, logging out...');
          setUser(null);
          setTasks([]);
          setLoading(false);
        }
      } else if (event.key === 'user' && event.newValue) {
        // User data was updated in another tab
        try {
          const userData = JSON.parse(event.newValue);
          console.log('User data updated in another tab:', userData);
          setUser(userData);
        } catch (error) {
          console.error('Error parsing user data from localStorage:', error);
        }
      }
    };

    // Listen for storage changes (from other tabs)
    window.addEventListener('storage', handleStorageChange);

    // Listen for automatic logout events from API interceptor
    const handleAutoLogout = () => {
      console.log('Automatic logout event received, cleaning up app state...');
      setUser(null);
      setTasks([]);
      setLoading(false);
    };

    window.addEventListener('userLogout', handleAutoLogout);

    // Reduced frequency focus check - only check if user is null but token exists
    const handleFocus = () => {
      const token = localStorage.getItem('token');
      if (token && !user) {
        console.log('Tab became active and user is null but token exists, checking auth status...');
        checkAuthStatus(false);
      }
    };

    // Debounce focus events to prevent excessive calls
    let focusTimeout;
    const debouncedHandleFocus = () => {
      clearTimeout(focusTimeout);
      focusTimeout = setTimeout(handleFocus, 1000); // Wait 1 second before checking
    };

    window.addEventListener('focus', debouncedHandleFocus);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userLogout', handleAutoLogout);
      window.removeEventListener('focus', debouncedHandleFocus);
      if (focusTimeout) clearTimeout(focusTimeout);
    };
  }, [user]); // Include user in dependency array to prevent unnecessary checks

  // Load tasks for the current date when authenticated
  useEffect(() => {
    console.log('=== useEffect for loadTasks triggered ===');
    console.log('User state:', user);
    console.log('User exists:', !!user);
    console.log('User ID:', user?.id);
    console.log('Current date:', currentDate);
    
    if (user) {
      console.log('User authenticated, calling loadTasks');
      // Reset pagination when date changes
      setCurrentPage(1);
      setHasMoreTasks(true);
      loadTasks(1, false); // Load first page, don't append
    } else {
      console.log('User not authenticated, skipping loadTasks');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, currentDate]);

  const loadTasks = async (page = 1, append = false) => {
    try {
      console.log('=== LOADING TASKS DEBUG ===');
      console.log('Loading tasks for page:', page);
      console.log('Append mode:', append);
      console.log('Current date:', currentDate.toISOString().split('T')[0]);
      console.log('User object:', user);
      
      // Check authentication before making API call
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No authentication token found, skipping task loading');
        setTasks([]);
        setHasMoreTasks(false);
        return;
      }
      
      if (!user || !user.id) {
        console.warn('User not properly authenticated, skipping task loading');
        setTasks([]);
        setHasMoreTasks(false);
        return;
      }
      
      console.log('Authentication confirmed, proceeding with API call');
      console.log('Token exists:', !!token);
      console.log('User ID:', user.id);
      
      // Set loading state
      if (append) {
        setIsLoadingMore(true);
      }
      
      // The API loads all tasks with pagination
      const response = await taskService.getTasks({ page, per_page: TASKS_PER_PAGE });
      console.log('=== API RESPONSE DEBUG ===');
      console.log('Full API response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);
      
      // Handle different possible response structures and flatten the grouped data
      let tasksData = [];
      let hasMore = true;
      
      if (response.data && response.data.data) {
        // The API returns grouped data: [{ due_date: "2025-01-06", tasks: [...] }]
        // We need to flatten this to get all individual tasks
        const groupedData = response.data.data;
        console.log('=== GROUPED DATA PROCESSING ===');
        console.log('Grouped data:', groupedData);
        console.log('Grouped data length:', groupedData.length);
        
        if (Array.isArray(groupedData)) {
          groupedData.forEach((group, index) => {
            console.log(`Processing group ${index}:`, group);
            if (group && group.tasks && Array.isArray(group.tasks)) {
              console.log(`Group ${index} has ${group.tasks.length} tasks`);
              // Add due_date and other group info to each task for easier processing
              const tasksWithDate = group.tasks.map(task => {
                const processedTask = {
                  ...task,
                  // Preserve individual task due_date, only use group due_date as fallback
                  due_date: task.due_date || group.due_date,
                  // Make sure we have required fields
                  name: task.name || task.title || 'Untitled Task',
                  project_name: task.project_name || task.project?.name || 'Personal',
                  // Ensure project_id is available for delete operations
                  project_id: task.project_id || task.project?.id || group.project_id || 1
                };
                console.log(`Task "${processedTask.name}" - Original due_date: ${task.due_date}, Group due_date: ${group.due_date}, Final due_date: ${processedTask.due_date}, Project ID: ${processedTask.project_id}`);
                return processedTask;
              });
              tasksData.push(...tasksWithDate);
              console.log(`Added ${tasksWithDate.length} tasks from group ${index}`);
            } else {
              console.log(`Group ${index} has no tasks or invalid tasks:`, group?.tasks);
            }
          });
        }
        console.log('Flattened tasks from grouped data:', tasksData);
        
        // Check for pagination info
        if (response.data.pagination) {
          hasMore = response.data.pagination.has_more !== false;
        } else {
          // Estimate if there are more based on response size
          hasMore = tasksData.length >= TASKS_PER_PAGE;
        }
      } else if (response.data && Array.isArray(response.data)) {
        // Handle case where data is directly an array
        tasksData = response.data.map(task => ({
          ...task,
          name: task.name || task.title || 'Untitled Task',
          project_name: task.project_name || task.project?.name || 'Personal',
          project_id: task.project_id || task.project?.id || 1
        }));
        console.log('Using response.data directly:', tasksData);
        hasMore = tasksData.length >= TASKS_PER_PAGE;
      } else if (response.data) {
        // Handle case where response.data exists but is not in expected format
        console.log('Response data exists but not in expected format:', response.data);
        // Try to extract tasks from any possible structure
        if (response.data.tasks && Array.isArray(response.data.tasks)) {
          tasksData = response.data.tasks.map(task => ({
            ...task,
            name: task.name || task.title || 'Untitled Task',
            project_name: task.project_name || task.project?.name || 'Personal',
            project_id: task.project_id || task.project?.id || 1
          }));
          console.log('Found tasks in response.data.tasks:', tasksData);
        } else if (Array.isArray(response.data)) {
          tasksData = response.data.map(task => ({
            ...task,
            name: task.name || task.title || 'Untitled Task',
            project_name: task.project_name || task.project?.name || 'Personal',
            project_id: task.project_id || task.project?.id || 1
          }));
          console.log('Treating response.data as task array:', tasksData);
        } else {
          console.log('Could not find tasks in response.data structure');
          tasksData = [];
        }
        hasMore = tasksData.length >= TASKS_PER_PAGE;
      } else {
        console.log('No valid task data found in response');
        tasksData = [];
        hasMore = false;
      }
      
      console.log('=== FINAL PROCESSING ===');
      console.log('Raw tasks data before filtering:', tasksData);
      console.log('Tasks data type:', typeof tasksData, 'Is array:', Array.isArray(tasksData));
      
      // Filter to ensure we only have valid task objects
      const validTasks = Array.isArray(tasksData) ? tasksData.filter(task => {
        const isValid = task && typeof task === 'object' && (task.id || task.name);
        if (!isValid) {
          console.warn('Filtering out invalid task:', task);
        }
        return isValid;
      }) : [];
      
      console.log('Valid tasks after filtering:', validTasks);
      console.log('Number of valid tasks:', validTasks.length);
      console.log('Has more tasks:', hasMore);
      console.log('=== END TASK LOADING DEBUG ===');
      
      // Update state based on append mode
      if (append) {
        setTasks(prev => [...prev, ...validTasks]);
      } else {
        setTasks(validTasks);
      }
      
      setHasMoreTasks(hasMore && validTasks.length > 0);
      
    } catch (error) {
      console.error('=== TASK LOADING ERROR ===');
      console.error('Failed to load tasks:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      
      // Handle specific error types
      if (error.response?.status === 401) {
        console.error('Authentication error - user may need to log in again');
        // Could trigger a logout here if needed
      } else if (error.response?.status === 403) {
        console.error('Permission error - user may not have access to tasks');
      } else if (!error.response) {
        console.error('Network error - API may be unreachable');
      }
      
      console.error('=== END ERROR DEBUG ===');
      
      // On error, don't append, just set empty array for first page
      if (!append) {
        setTasks([]);
      }
      setHasMoreTasks(false);
    } finally {
      if (append) {
        setIsLoadingMore(false);
      }
    }
  };

  // Function to load more tasks for infinite scroll
  const loadMoreTasks = async () => {
    if (!hasMoreTasks || isLoadingMore) {
      return;
    }
    
    console.log('Loading more tasks, current page:', currentPage);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await loadTasks(nextPage, true); // true for append mode
  };

  const handlePrevDay = () => {
    console.log('=== PREV DAY CLICKED ===');
    console.log('Current date before:', currentDate);
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() - 1);
      console.log('New date after prev day:', newDate);
      return newDate;
    });
  };

  const handleNextDay = () => {
    console.log('=== NEXT DAY CLICKED ===');
    console.log('Current date before:', currentDate);
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + 1);
      console.log('New date after next day:', newDate);
      return newDate;
    });
  };

  const handleAddTask = async (taskData) => {
    try {
      const response = await taskService.createTask(taskData);
      console.log('Create task response:', response);
      
      // Handle different possible response structures
      let newTask;
      if (response.data && response.data.task) {
        newTask = response.data.task;
      } else if (response.data) {
        newTask = response.data;
      } else {
        newTask = response;
      }
      
      // Ensure the new task has required properties
      if (newTask && typeof newTask === 'object') {
        setTasks(prev => {
          const updatedTasks = [...prev, newTask];
          
          // Sort tasks with defensive checks
          return updatedTasks.sort((a, b) => {
            // Check if both tasks exist and have time properties
            if (!a || !b) return 0;
            if (!a.time && !b.time) return 0;
            if (!a.time) return 1;
            if (!b.time) return -1;
            
            try {
              const timeA = new Date(`1970-01-01T${a.time}`);
              const timeB = new Date(`1970-01-01T${b.time}`);
              return timeA - timeB;
            } catch (error) {
              console.error('Error sorting tasks by time:', error);
              return 0;
            }
          });
        });
        setIsModalOpen(false);
        setEditingTask(null);
        
        // Reload tasks from first page after adding new task
        setCurrentPage(1);
        setHasMoreTasks(true);
        await loadTasks(1, false);
      } else {
        console.error('Invalid task data received:', newTask);
        alert('Task created but could not display. Please refresh the page.');
      }
    } catch (error) {
      console.error('Failed to add task:', error);
      
      // Provide specific error messages based on response
      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          alert(`Failed to create task: ${data.message || 'Please check all required fields are filled correctly'}`);
        } else if (status === 404) {
          alert('API endpoint not found. Please check your server configuration.');
        } else if (status === 401 || status === 403) {
          alert('You are not authorized to create tasks. Please log in again.');
        } else {
          alert(`Server error (${status}): ${data.message || 'Please try again later'}`);
        }
      } else {
        alert('Network error: Please check your internet connection and try again.');
      }
    }
  };

  const handleEditTask = async (taskData) => {
    try {
      if (!editingTask || !editingTask.id) {
        // If no editing task is set, this is for opening the edit modal
        setEditingTask(taskData);
        setIsModalOpen(true);
        return;
      }

      // Ensure we have the project_id for the update
      const projectId = editingTask.project_id || taskData.project_id || 1;
      
      const response = await taskService.updateTask(editingTask.id, taskData, projectId);
      console.log('Update task response:', response);
      
      // Update the task in the local state
      setTasks(prev => prev.map(task => 
        task.id === editingTask.id ? { ...task, ...taskData } : task
      ));
      
      setIsModalOpen(false);
      setEditingTask(null);
      
      // Reload tasks from first page after editing task
      setCurrentPage(1);
      setHasMoreTasks(true);
      await loadTasks(1, false);
    } catch (error) {
      console.error('Failed to edit task:', error);
      
      // Provide specific error messages
      if (error.response) {
        const { status, data } = error.response;
        if (status === 400) {
          alert(`Failed to update task: ${data.message || 'Please check all fields are filled correctly'}`);
        } else if (status === 404) {
          alert('Task not found. It may have been deleted.');
        } else if (status === 401 || status === 403) {
          alert('You are not authorized to update this task.');
        } else {
          alert(`Server error (${status}): ${data.message || 'Please try again later'}`);
        }
      } else {
        alert('Network error: Please check your internet connection and try again.');
      }
    }
  };

  const handleDeleteTask = async (taskToDelete) => {
    try {
      console.log('=== DELETE TASK DEBUG ===');
      console.log('Task to delete:', taskToDelete);
      console.log('Task ID:', taskToDelete.id);
      
      if (!taskToDelete.id) {
        console.error('Task ID not found:', taskToDelete);
        alert('Cannot delete task: ID not found.');
        return;
      }

      console.log('Calling deleteTask with ID:', taskToDelete.id);
      
      // According to CSV documentation, DELETE /task/<int:task_id> only needs task ID
      await taskService.deleteTask(taskToDelete.id);
      
      // Remove the task from local state
      setTasks(prev => prev.filter(task => task.id !== taskToDelete.id));
      
      console.log('Task deleted successfully');
    } catch (error) {
      console.error('=== DELETE TASK ERROR ===');
      console.error('Failed to delete task:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Task data that failed:', taskToDelete);
      
      // Provide specific error messages
      if (error.response?.status === 404) {
        alert('Task not found. It may have already been deleted.');
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        alert('You are not authorized to delete this task.');
      } else if (error.response?.status === 405) {
        alert('Delete operation not allowed. The API endpoint may not support task deletion.');
      } else if (error.response) {
        const { status, data } = error.response;
        alert(`Server error (${status}): ${data.message || 'Failed to delete task. Please try again.'}`);
      } else {
        alert('Network error: Please check your internet connection and try again.');
      }
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      // Find the task to get its project_id
      const task = tasks.find(t => t.id === taskId);
      
      if (!task) {
        console.error('Task not found for status update');
        return;
      }
      
      await taskService.patchTask(taskId, { status: newStatus });
      
      // Update the task status in local state
      setTasks(prev => prev.map(t => 
        t.id === taskId ? { ...t, status: newStatus } : t
      ));
      
      console.log('Task status updated successfully');
    } catch (error) {
      console.error('Failed to update task status:', error);
      alert('Failed to update task status. Please try again.');
    }
  };

  const handleLoginSuccess = (userData) => {
    console.log('Login successful, updating user state and localStorage');
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    // Trigger a custom event to notify other components if needed
    window.dispatchEvent(new Event('userLogin'));
  };

  const handleLogout = () => {
    console.log('Logging out, clearing state and localStorage');
    setUser(null);
    setTasks([]);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Trigger a custom event to notify other components if needed
    window.dispatchEvent(new Event('userLogout'));
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  // Protected route component
  const PrivateRoute = ({ children }) => {
    // Check both token and user state for authentication
    const token = localStorage.getItem('token');
    return token && user ? children : <Navigate to="/login" />;
  };

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/" element={localStorage.getItem('token') ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />} />
        
        <Route path="/dashboard" element={
          <PrivateRoute>
            <div className="container">
            <Dashboard
              tasks={tasks}
              isModalOpen={isModalOpen}
              currentDate={currentDate}
              user={user}
              handlePrevDay={handlePrevDay}
              handleNextDay={handleNextDay}
              handleAddTask={handleAddTask}
              handleEditTask={handleEditTask}
              handleDeleteTask={handleDeleteTask}
              handleStatusChange={handleStatusChange}
              setIsModalOpen={setIsModalOpen}
              setEditingTask={setEditingTask}
              editingTask={editingTask}
              handleLogout={handleLogout}
              onLoadMoreTasks={loadMoreTasks}
              hasMoreTasks={hasMoreTasks}
              isLoadingMore={isLoadingMore}
            />
            </div>
          </PrivateRoute>
        } />
        
        <Route path="/accept-invite" element={
          user ? <Navigate to="/dashboard" /> : <AcceptInvite onAcceptSuccess={handleLoginSuccess} />
        } />
        
        <Route path="/invites" element={
          <PrivateRoute>
            <div className="container">
              <Header 
                onPrevDay={handlePrevDay}
                onNextDay={handleNextDay}
                currentDate={currentDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                user={user}
                onLogout={handleLogout}
              />
              <InviteManager />
            </div>
          </PrivateRoute>
        } />
        
        <Route path="/project-manager" element={
          <PrivateRoute>
            <div className="container">
              <Header 
                onPrevDay={handlePrevDay}
                onNextDay={handleNextDay}
                currentDate={currentDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                user={user}
                onLogout={handleLogout}
              />
              <ProjectManager />
            </div>
          </PrivateRoute>
        } />
        
        <Route path="/user-manager" element={
          <PrivateRoute>
            <div className="container">
              <Header 
                onPrevDay={handlePrevDay}
                onNextDay={handleNextDay}
                currentDate={currentDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                user={user}
                onLogout={handleLogout}
              />
              <UserManager />
            </div>
          </PrivateRoute>
        } />
      </Routes>
    </Router>
  );
}

export default App;
